import {
  getAriaNotificationMeta,
  isAriaNotificationForRole,
} from '../constants/ariaNotificationTypes';
import {
  getEventNotificationMeta,
  isEventNotificationForRole,
} from '../constants/eventNotificationTypes';
import {
  getCommunityNotificationMeta,
  isCommunityNotificationForRole,
} from '../constants/communityNotificationTypes';
import {
  getVettingNotificationMeta,
  isVettingNotificationForRole,
} from '../constants/vettingNotificationTypes';
import {
  getPartnerNotificationMeta,
  isPartnerNotificationForRole,
} from '../constants/partnerNotificationTypes';
import { pickNotificationCreatedAt } from '../components/Notifications/notificationTimeFormat';
import { resolveNotificationTitle } from './notificationDisplay';
import { pickNotificationChannelId } from './notificationReadActions';
import { pickNotificationId } from './pickNotificationId';
import {
  isAriaPortalNotification,
  isCommunityPortalNotification,
  isPartnerPortalNotification,
  isSupportedPortalNotification,
  isVettingPortalNotification,
  pickNotificationEventId,
  resolvePortalNotificationType,
} from './portalNotificationRouting';

export const isPortalNotificationListItem = (item) => item.id != null || item.channelId != null;

export const getPortalNotificationListItemKey = (item, index = 0) =>
  item.id ??
  `${item.type ?? 'notification'}-${item.channelId ?? 'no-channel'}-${item.createdAt ?? index}`;

export const isPortalNotificationForRole = (item, userRole) => {
  if (!userRole) {
    return true;
  }
  if (isAriaPortalNotification(item)) {
    return isAriaNotificationForRole(item.type || item.targetType, userRole);
  }
  if (isVettingPortalNotification(item)) {
    return isVettingNotificationForRole(item.type || item.targetType, userRole);
  }
  if (isPartnerPortalNotification(item)) {
    return isPartnerNotificationForRole(item.type || item.targetType, userRole);
  }
  if (isCommunityPortalNotification(item)) {
    return isCommunityNotificationForRole(item.type, userRole);
  }
  return isEventNotificationForRole(item.type, userRole);
};

export const mapPortalNotificationItem = (raw = {}) => {
  const nested = raw.data ?? {};
  const targetType = raw.targetType ?? nested.targetType ?? null;
  const type = resolvePortalNotificationType({ ...nested, ...raw, targetType });
  const meta =
    getEventNotificationMeta(type) ??
    getCommunityNotificationMeta(type) ??
    getVettingNotificationMeta(type) ??
    getPartnerNotificationMeta(type) ??
    getAriaNotificationMeta(type);
  const channelId = pickNotificationChannelId({ ...nested, ...raw, type, targetType });

  const actor = raw.actor ?? nested.actor ?? {};
  const actorFullName =
    actor.fullName ??
    actor.name ??
    raw.actorName ??
    nested.reviewerName ??
    raw.reviewerName ??
    null;
  const actorAvatarUrl = actor.avatarUrl ?? actor.avatar ?? null;

  return {
    id: pickNotificationId(raw),
    type,
    title: resolveNotificationTitle({ targetType, type }),
    eventId: pickNotificationEventId(raw),
    targetId: raw.targetId ?? nested.targetId ?? null,
    targetType,
    channelId,
    actorFullName: actorFullName ? String(actorFullName) : null,
    actorAvatarUrl: actorAvatarUrl ? String(actorAvatarUrl) : null,
    createdAt: pickNotificationCreatedAt(raw),
    read: Boolean(raw.isRead ?? raw.read ?? raw.readAt ?? raw.read_at),
    meta,
  };
};

const extractNotificationList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  const root = data?.data ?? data;
  if (Array.isArray(root)) {
    return root;
  }
  if (Array.isArray(root?.notifications)) {
    return root.notifications;
  }
  if (Array.isArray(root?.items)) {
    return root.items;
  }

  return [];
};

/** Map GET /admin/notifications?audience=portal&limit=&page= response. */
export const mapPortalNotificationsPage = (data, { limit, page = 1, userRole = null }) => {
  const list = extractNotificationList(data);
  const notifications = list
    .map(mapPortalNotificationItem)
    .filter(isPortalNotificationListItem)
    .filter((item) => isSupportedPortalNotification(item))
    .filter((item) => isPortalNotificationForRole(item, userRole));

  const root = Array.isArray(data) ? null : (data?.data ?? data);
  const totalRaw = root?.total ?? data?.total;
  const total = totalRaw != null && !Number.isNaN(Number(totalRaw)) ? Number(totalRaw) : null;
  const currentPage = Number(page) > 0 ? Number(page) : 1;

  const hasMore =
    total != null
      ? (currentPage - 1) * limit + notifications.length < total
      : notifications.length >= limit;

  return {
    notifications,
    total,
    page: currentPage,
    limit,
    hasMore,
  };
};
